/**
 * ReplayMode - Main replay container (vue simple, une table).
 *
 * Renders INSIDE .game-table (not as a full-screen overlay).
 * La machine à états de lecture vit dans useReplayPlayback, partagée avec
 * ReplayDualMode.
 */

import { useState, useMemo } from 'react';
import ReplayPane from './ReplayPane';
import ReplayControls from './ReplayControls';
import ReplayTimeline from './ReplayTimeline';
import type { GameHistory } from '../game/gameHistory';
import { useReplayPlayback } from '../hooks/useReplayPlayback';
import { ContractNames } from '../types/game';
import '../styles/replay.css';

interface ReplayModeProps {
  history: GameHistory;
  /** Callback "Prêt pour la suite" (affiché dans le header quand sidebar masquée) */
  onReady?: () => void;
  isReady?: boolean;
  isLive?: boolean;
  /** Contenu du popup de score complet (ScorePanel sans bouton Prêt) */
  scorePopup?: React.ReactNode;
}

export default function ReplayMode({ history, onReady, isReady, isLive, scorePopup }: ReplayModeProps) {
  const playback = useReplayPlayback(history);
  const { steps, stepIndex, subStep, maxSubStep, currentStep } = playback;

  const [showScorePopup, setShowScorePopup] = useState(false);

  const stepLabel = useMemo(() => {
    switch (currentStep?.type) {
      case 'distribution':
        return 'Distribution';
      case 'chien':
        return 'Le Chien';
      case 'ecart':
        return "L'Ecart";
      case 'trick':
        return `Pli ${currentStep.trickNumber} — ${subStep}/${maxSubStep} cartes`;
      default:
        return '';
    }
  }, [currentStep, subStep, maxSubStep]);

  const contractName = ContractNames[history.contract] || '';
  const takerName = history.players[history.taker]?.name || '';

  const header = (
    <div className="replay-header">
      <div className="replay-header-info">
        <span>Replay</span>
        {scorePopup ? (
          <button
            className="replay-contract replay-contract-clickable"
            onClick={() => setShowScorePopup(true)}
            title="Voir le détail du score"
          >
            {contractName} par {takerName}
          </button>
        ) : (
          <span className="replay-contract">
            {contractName} par {takerName}
          </span>
        )}
        <span>{stepLabel}</span>
      </div>
      {/* Bouton "Prêt" dans le header quand sidebar masquée */}
      {onReady && !isLive && (
        <button
          className={`replay-ready-btn ${isReady ? 'ready' : ''}`}
          onClick={onReady}
          disabled={isReady}
        >
          {isReady ? 'En attente…' : 'Prêt ✓'}
        </button>
      )}
    </div>
  );

  return (
    <div className="replay-container">
      <div className="replay-main" ref={playback.gestureRef}>
        <ReplayPane
          history={history}
          stepIndex={stepIndex}
          subStep={subStep}
          header={header}
        />

        {/* Popup score complet */}
        {showScorePopup && scorePopup && (
          <div className="replay-score-popup-backdrop" onClick={() => setShowScorePopup(false)}>
            <div className="replay-score-popup" onClick={e => e.stopPropagation()}>
              <button className="replay-score-popup-close" onClick={() => setShowScorePopup(false)}>✕</button>
              {scorePopup}
            </div>
          </div>
        )}

        <ReplayControls playback={playback} />
      </div>

      {/* Timeline sidebar */}
      <ReplayTimeline
        history={history}
        steps={steps}
        activeStepIndex={stepIndex}
        onStepClick={playback.handleTimelineClick}
      />
    </div>
  );
}
